<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="800"
    scrollable
    content-class="parchment-detail-dialog"
    @click:outside="close"
  >
    <v-card v-if="detail || fetchLoading" class="parchment-detail-card">
      <v-toolbar color="secondary" class="white--text" flat height="48">
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.parchmentDelivery.bulkImport.detailTitle") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text v-if="fetchLoading" class="d-flex justify-center align-center py-12">
        <v-progress-circular indeterminate color="primary" size="48" />
      </v-card-text>

      <v-card-text v-else class="pa-0 parchment-detail-dialog__body">
        <section class="parchment-detail-section px-6 pt-5 pb-4">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="parchment-detail-section__title mb-0">
              {{ $t("memberDataSection.parchmentDelivery.bulkImport.parchmentDeliverySection") }}
            </h3>
            <div class="d-flex align-center" style="gap: 8px;">
              <v-btn outlined color="secondary" height="36" class="text-none px-3" @click="onViewHistory">
                {{ $t("memberDataSection.parchmentDelivery.viewHistory") }}
              </v-btn>
              <v-btn
                v-if="!isDelivered"
                color="primary"
                height="36"
                depressed
                class="text-uppercase white--text px-5"
                @click="onEdit"
              >
                {{ $t("edit") }}
              </v-btn>
            </div>
          </div>
          <div
            v-if="showMissingOutturnAlert"
            class="parchment-outturn-mismatch-alert mb-5"
          >
            <div class="d-flex align-start">
              <v-icon color="#FFA92A" class="mr-2 mt-1">mdi-alert</v-icon>
              <div>
                <p class="parchment-outturn-mismatch-alert__title mb-1">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.issueMissingOutturnNumberTitle") }}
                </p>
                <p class="parchment-outturn-mismatch-alert__body mb-0">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.issueMissingOutturnNumberDescription") }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="showOutturnMismatchAlert"
            class="parchment-outturn-mismatch-alert mb-5"
          >
            <div class="d-flex align-start">
              <v-icon color="#FFA92A" class="mr-2 mt-1">mdi-alert</v-icon>
              <div>
                <p class="parchment-outturn-mismatch-alert__title mb-1">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.issueOutturnNumberMismatchTitle") }}
                </p>
                <p class="parchment-outturn-mismatch-alert__body mb-0">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.issueOutturnNumberMismatchDedicatedDescription") }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="showDuplicateOutturnAlert"
            class="parchment-outturn-mismatch-alert mb-5"
          >
            <div class="d-flex align-start">
              <v-icon color="#FFA92A" class="mr-2 mt-1">mdi-alert</v-icon>
              <div>
                <p class="parchment-outturn-mismatch-alert__title mb-1">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.issueDuplicateOutturnNumberTitle") }}
                </p>
                <p class="parchment-outturn-mismatch-alert__body mb-0">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.issueDuplicateOutturnNumberDescription") }}
                </p>
              </div>
            </div>
          </div>

          <!-- Discrepancy resolution -->
          <div v-if="showResolutionPanel" class="parchment-resolution-panel mb-5">
            <div class="d-flex align-start mb-3">
              <v-icon color="#FFA92A" class="mr-2 mt-1">mdi-alert</v-icon>
              <div>
                <p class="parchment-resolution-panel__title mb-1">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.discrepancyResolutionTitle") }}
                </p>
                <p class="parchment-resolution-panel__subtitle mb-0">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.discrepancyResolutionSubtitle") }}
                </p>
              </div>
            </div>

            <div class="parchment-resolution-panel__inner">
              <p class="parchment-resolution-panel__prompt mb-3">
                {{ $t("memberDataSection.parchmentDelivery.bulkImport.discrepancyResolutionPrompt") }}
              </p>

              <div class="parchment-resolution-table">
                <div class="parchment-resolution-table__header">
                  <span></span>
                  <span>{{ $t("memberDataSection.parchmentDelivery.bulkImport.factoryRecord") }}</span>
                  <span>{{ $t("memberDataSection.parchmentDelivery.bulkImport.unionRecord") }}</span>
                </div>
                <div
                  v-for="conflict in detail.resolutionConflicts"
                  :key="conflict.fieldKey"
                  class="parchment-resolution-table__row"
                >
                  <span class="parchment-resolution-table__field-label">
                    {{ getConflictFieldLabel(conflict.fieldKey) }}
                  </span>
                  <label class="parchment-resolution-table__choice">
                    <input
                      v-model="resolutionSelections[conflict.fieldKey]"
                      type="radio"
                      :name="`resolution-${conflict.fieldKey}`"
                      value="factory"
                    />
                    <span>{{ conflict.factoryValue }}</span>
                  </label>
                  <label class="parchment-resolution-table__choice">
                    <input
                      v-model="resolutionSelections[conflict.fieldKey]"
                      type="radio"
                      :name="`resolution-${conflict.fieldKey}`"
                      value="uploaded"
                    />
                    <span>{{ conflict.uploadedValue }}</span>
                  </label>
                </div>
              </div>

              <div class="text-right mt-4">
                <v-btn
                  color="primary"
                  depressed
                  height="36"
                  class="text-uppercase white--text px-5"
                  :disabled="!canConfirmResolution"
                  @click="confirmResolution"
                >
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.confirmAndSave") }}
                </v-btn>
              </div>
            </div>
          </div>

          <div
            v-for="(row, rowIndex) in deliveryFieldRows"
            :key="`delivery-row-${rowIndex}`"
            class="parchment-detail-grid-row"
            :class="{ 'parchment-detail-grid-row--spaced': rowIndex > 0 }"
          >
            <div
              v-for="field in row"
              :key="field.key"
              class="parchment-detail-grid-cell"
            >
              <div class="field-label">{{ field.label }}</div>
              <div class="d-flex align-center flex-wrap field-value-row">
                <span class="field-value">
                  {{ field.displayValue }}
                </span>
                <v-tooltip
                  v-if="field.hasIssue"
                  bottom
                  max-width="360"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      color="#FFA92A"
                      class="ml-1 flex-shrink-0"
                    >
                      mdi-alert
                    </v-icon>
                  </template>
                  <div class="parchment-detail-issue-tooltip">
                    <div class="font-weight-bold">{{ field.issueTitle }}</div>
                    <div class="mt-1">{{ field.issueDescription }}</div>
                  </div>
                </v-tooltip>
              </div>
            </div>
          </div>

          <div class="parchment-detail-grid-row parchment-detail-grid-row--spaced">
            <div class="parchment-detail-grid-cell">
              <div class="field-label">{{ $t("status") }}</div>
              <span class="status-pill" :class="statusPillClass">{{ deliveryStatusLabel }}</span>
            </div>
          </div>
        </section>

        <v-divider class="parchment-detail-divider"></v-divider>

        <section class="parchment-detail-section px-6 py-4">
            <h3 class="parchment-detail-plain-title mb-3">
              {{ $t("memberDataSection.parchmentDelivery.bulkImport.processingBatchLot") }}
            </h3>
            <template v-if="detail.processingBatchLots && detail.processingBatchLots.length">
              <div
                v-for="(batch, batchIndex) in detail.processingBatchLots"
                :key="`batch-lot-${batchIndex}`"
                class="parchment-detail-grid-row"
                :class="{ 'parchment-detail-grid-row--spaced': batchIndex > 0 }"
              >
                <div class="parchment-detail-grid-cell">
                  <div class="field-label">{{ $t("memberDataSection.greenCoffee.batchId") }}</div>
                  <div class="field-value mt-1">{{ batch.batchId }}</div>
                </div>
                <div class="parchment-detail-grid-cell">
                  <div class="field-label">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.typeAndProcess") }}
                  </div>
                  <div class="field-value mt-1">{{ batch.speciesVariety }}</div>
                  <div class="field-value-secondary">{{ batch.processLine }}</div>
                </div>
                <div class="parchment-detail-grid-cell">
                  <div class="field-label">{{ $t("memberDataSection.greenCoffee.quantity") }}</div>
                  <div class="field-value mt-1">{{ batch.quantity }}</div>
                </div>
              </div>
            </template>
            <div v-else class="parchment-detail-grid-row">
              <div class="parchment-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.greenCoffee.batchId") }}</div>
                <div class="field-value mt-1">{{ detail.processingBatchLot.batchId }}</div>
              </div>
              <div class="parchment-detail-grid-cell">
                <div class="field-label">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.typeAndProcess") }}
                </div>
                <div class="field-value mt-1">{{ detail.processingBatchLot.speciesVariety }}</div>
                <div class="field-value-secondary">{{ detail.processingBatchLot.processLine }}</div>
              </div>
              <div class="parchment-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.greenCoffee.quantity") }}</div>
                <div class="field-value mt-1">{{ detail.processingBatchLot.quantity }}</div>
              </div>
            </div>
          </section>

          <v-divider class="parchment-detail-divider"></v-divider>

          <section class="parchment-detail-section px-6 py-4">
            <h3 class="parchment-detail-plain-title mb-4">
              {{ $t("memberDataSection.greenCoffee.qrCode") }}
            </h3>
            <div class="parchment-detail-qr-row">
              <div v-if="showQrContent" class="parchment-detail-qr-group">
                <div class="parchment-detail-qr-block text-center">
                  <qrcode
                    v-if="detail.qrCodes.internalValue"
                    :text="detail.qrCodes.internalValue"
                    :size="108"
                  ></qrcode>
                  <div v-else class="field-value">—</div>
                  <p class="parchment-detail-qr-label mb-0 mt-2">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.internalId") }}:
                    {{ detail.qrCodes.internalId }}
                  </p>
                </div>
                <div class="parchment-detail-qr-block text-center">
                  <qrcode
                    v-if="detail.qrCodes.externalValue"
                    :text="detail.qrCodes.externalValue"
                    :size="108"
                  ></qrcode>
                  <div v-else class="field-value">—</div>
                  <p class="parchment-detail-qr-label mb-0 mt-2">
                    {{ $t("memberDataSection.greenCoffee.externalId") }}:
                    {{ detail.qrCodes.externalId }}
                  </p>
                </div>
              </div>
              <v-btn
                v-if="showQrContent"
                outlined
                color="primary"
                height="36"
                class="text-none px-5"
                @click="downloadQrCodes"
              >
                {{ $t("memberDataSection.parchmentDelivery.bulkImport.downloadQr") }}
              </v-btn>
            </div>
          </section>
      </v-card-text>

      <template v-if="!fetchLoading">
      <v-divider class="parchment-detail-divider"></v-divider>
      <v-card-actions class="px-6 py-3 parchment-detail-footer">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          height="36"
          depressed
          class="text-uppercase white--text px-5"
          @click="close"
        >
          {{ $t("memberDataSection.greenCoffee.close") }}
        </v-btn>
      </v-card-actions>
      <ParchmentDeliveryHistoryDialog
        v-model="historyDialogVisible"
        :record="record"
        @close="historyDialogVisible = false"
      />
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  BULK_ISSUE_TYPE,
  BULK_ISSUE_I18N_KEYS,
  buildParchmentDeliveryDetailView,
  formatParchmentFieldDisplay,
  getResolvableFieldConflicts,
  mergeResolutionIntoPayload,
} from "@/utils/parchmentDeliveryBulkImport";
import ParchmentDeliveryHistoryDialog from "./HistoryDialog.vue";
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";

const FIELD_LABEL_KEYS = {
  lotId: "lotId",
  outturnNumber: "outturnNumber",
  deliveryDate: "deliveryDate",
  season: "season",
  growerName: "growerName",
  growerCode: "growerCode",
  coffeeType: "coffeeType",
  bags: "bags",
  grossWeight: "grossWeight",
  netWeightWithBags: "netWeightWithBags",
  netWeight: "netWeight",
  broker: "broker",
};

export default {
  name: "ParchmentDeliveryDetailDialog",
  components: { ParchmentDeliveryHistoryDialog },
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
  },
  data() {
    return {
      visible: this.value,
      historyDialogVisible: false,
      sections: {},
      resolutionSelections: {},
      activePayloadOverride: null,
      fetchedRecord: null,
      fetchLoading: false,
    };
  },
  computed: {
    detail() {
      if (this.fetchedRecord) return this.buildDetailFromApiRecord(this.fetchedRecord);
      if (!this.record) return null;
      return buildParchmentDeliveryDetailView(this.record, this.activePayloadOverride);
    },
    baseI18n() {
      return "memberDataSection.parchmentDelivery.bulkImport";
    },
    showResolutionPanel() {
      return Boolean(this.detail?.hasResolvableDiscrepancy);
    },
    showMissingOutturnAlert() {
      return (
        this.detail?.statusType === "issues" &&
        this.detail?.issueType === BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER
      );
    },
    showOutturnMismatchAlert() {
      return (
        this.detail?.statusType === "issues" &&
        this.detail?.issueType === BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH
      );
    },
    isOutturnHardBlock() {
      return (
        this.detail?.statusType === "issues" &&
        (this.detail?.issueType === BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH ||
          this.detail?.issueType === BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER)
      );
    },
    showQrContent() {
      return Boolean(
        this.detail?.qrCodes?.internalValue || this.detail?.qrCodes?.externalValue
      );
    },
    showDuplicateOutturnAlert() {
      return (
        this.detail?.statusType === "issues" &&
        this.detail?.issueType === BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER
      );
    },
    canConfirmResolution() {
      if (!this.detail?.resolutionConflicts?.length) return false;
      return this.detail.resolutionConflicts.every(
        (c) => this.resolutionSelections[c.fieldKey] === "factory" || this.resolutionSelections[c.fieldKey] === "uploaded"
      );
    },
    deliveryFieldRows() {
      if (!this.detail) return [];
      const t = (key) => this.$t(`${this.baseI18n}.${key}`);
      const d = this.detail;

      const payload = this.activePayloadOverride || this.record?.payloadJsonData || {};
      const buildField = (key, label, rawValue) => {
        const issueMeta = d.fieldIssues[key];
        const hasIssue = Boolean(issueMeta) && !this.record?.resolutionApplied;
        // The payload-based formatting only applies to bulk-import staging records
        // (raw uploaded values in `payload`). For an API-fetched delivery,
        // `buildDetailFromApiRecord` already returns display-ready values, so use
        // rawValue — otherwise these fields fall back to an empty payload and blank out.
        const usePayloadFormat =
          !this.fetchedRecord &&
          (key === "bags" || key === "deliveryDate" || key === "grossWeight" || key === "netWeight" || key === "netWeightWithBags" || key === "growerCode");
        const fallback = usePayloadFormat ? formatParchmentFieldDisplay(key, payload) : rawValue;
        return {
          key,
          label,
          displayValue: this.getFieldDisplayValue(key, fallback),
          hasIssue,
          issueTitle: hasIssue ? this.getIssueTitle(issueMeta) : "",
          issueDescription: hasIssue ? this.getIssueDescription(issueMeta) : "",
        };
      };

      return [
        [
          buildField("lotId", t("lotId"), d.lotId),
          buildField("outturnNumber", this.$t("memberDataSection.parchmentDelivery.outturnNumber"), d.outturnNumber),
          buildField("deliveryDate", t("deliveryDate"), d.deliveryDate),
        ],
        [
          buildField("season", t("season"), d.season),
          buildField("growerName", t("growerName"), d.growerName),
          buildField("growerCode", t("growerCode"), d.growerCode),
        ],
        [
          buildField("coffeeType", t("coffeeType"), d.coffeeType),
          buildField("bags", t("bags"), d.bags),
          buildField("grossWeight", t("grossWeight"), d.grossWeight),
        ],
        [
          buildField("netWeightWithBags", t("netWeightWithBags"), d.netWeightWithBags),
          buildField("netWeight", t("netWeight"), d.netWeight),
          buildField("broker", t("broker"), d.broker),
        ],
      ];
    },
    statusPillClass() {
      if (!this.detail) return "";
      if (this.detail.listStatusKey === "draft") return "status-pill--draft";
      if (this.detail.statusType === "success") return "status-pill--success";
      if (this.detail.statusType === "issues") return "status-pill--issues";
      return "status-pill--failed";
    },
    isDelivered() {
      const status = this.fetchedRecord?.status || this.record?.status;
      return String(status || "").toLowerCase() === "delivered";
    },
    deliveryStatusLabel() {
      if (!this.detail) return "";
      if (this.detail.listStatusKey === "delivered") {
        return this.$t("memberDataSection.parchmentDelivery.statusDelivered");
      }
      if (this.detail.listStatusKey === "draft") {
        return this.$t("memberDataSection.parchmentDelivery.statusDraft");
      }
      if (this.detail.statusType === "success") return this.$t("success");
      if (this.detail.statusType === "issues") {
        return this.$t("memberDataSection.parchmentDelivery.bulkImport.issuesDetected");
      }
      return this.$t("failed");
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        this.resetResolutionState();
        this.fetchRecord();
      } else {
        this.fetchedRecord = null;
      }
    },
    visible(v) {
      this.$emit("input", v);
    },
    record: {
      handler() {
        this.resetResolutionState();
      },
      deep: true,
    },
  },
  methods: {
    async fetchRecord() {
      const id = this.record?.id;
      if (!id) return;
      this.fetchLoading = true;
      try {
        const res = await ParchmentDeliveryService.getParchmentDelivery(id);
        this.fetchedRecord = res?.data?.parchmentDelivery || null;
      } catch (_) {
        this.fetchedRecord = null;
      } finally {
        this.fetchLoading = false;
      }
    },
    buildDetailFromApiRecord(pd) {
      if (!pd) return null;
      const fmt = (v) => (v != null ? String(v) : "—");
      const lotId = `PD-${String(pd.id).padStart(5, "0")}`;

      const batches = Array.isArray(pd.processingBatches) ? pd.processingBatches : [];
      const firstBatch = batches[0] || null;

      const batchId = firstBatch
        ? firstBatch.batchCode || `PB-${String(firstBatch.id).padStart(5, "0")}`
        : "—";

      let internalId = "—";
      let internalValue = "";
      try {
        const parsed = pd.internalQR ? JSON.parse(pd.internalQR) : null;
        if (parsed) {
          internalValue = pd.internalQR;
          internalId = parsed.lotId || lotId;
        }
      } catch (_) {
        internalValue = pd.internalQR || "";
        internalId = lotId;
      }

      const bags =
        pd.totalQuantity != null && pd.unitSize != null && pd.unitSize > 0
          ? Math.floor(pd.totalQuantity / pd.unitSize)
          : null;

      return {
        lotId,
        outturnNumber: fmt(pd.outturnNumber),
        deliveryDate: pd.deliveryDate
          ? new Date(pd.deliveryDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
          : "—",
        season: fmt(pd.season),
        growerName: fmt(pd.growersName),
        growerCode: fmt(pd.growersCode),
        coffeeType: fmt(pd.coffeeType),
        bags: bags != null ? String(bags) : "—",
        grossWeight: pd.grossWeight != null ? `${pd.grossWeight}${pd.grossWeightUnit ? " " + pd.grossWeightUnit : ""}` : "—",
        netWeightWithBags: pd.netWeightWithBag != null ? String(pd.netWeightWithBag) : "—",
        netWeight: pd.netWeight != null ? String(pd.netWeight) : "—",
        broker: fmt(pd.broker),

        processingBatchLots: batches.length
          ? batches.map((b) => ({
              batchId: b.batchCode || `PB-${String(b.id).padStart(5, "0")}`,
              speciesVariety: b.species || b.variety || b.processingType?.name || "—",
              processLine: b.processingType?.name || "—",
              quantity: b.parchmentOut != null ? fmt(b.parchmentOut) : (b.totalCoffeeCherryQty != null ? fmt(b.totalCoffeeCherryQty) : "—"),
            }))
          : [],

        processingBatchLot: {
          batchId,
          speciesVariety: firstBatch ? (firstBatch.species || firstBatch.variety || firstBatch.processingType?.name || "—") : "—",
          processLine: firstBatch ? (firstBatch.processingType?.name || "—") : "—",
          quantity: firstBatch ? (firstBatch.parchmentOut != null ? fmt(firstBatch.parchmentOut) : fmt(firstBatch.totalCoffeeCherryQty)) : "—",
        },

        processingBatchDetails: {
          startDate: firstBatch ? fmt(firstBatch.startDate) : "—",
          endDate: firstBatch ? fmt(firstBatch.endDate) : "—",
          totalCoffeeCherry: firstBatch ? fmt(firstBatch.totalCoffeeCherryQty) : "—",
          airHumidity: firstBatch ? fmt(firstBatch.airHumidity) : "—",
          density: firstBatch ? fmt(firstBatch.density) : "—",
          temperature: firstBatch ? fmt(firstBatch.temperature) : "—",
          beanMoistureContent: firstBatch ? fmt(firstBatch.beanMoistureContent) : "—",
          processType: firstBatch ? fmt(firstBatch.processingType?.name) : "—",
          batchRating: firstBatch ? fmt(firstBatch.batchRating) : "—",
          parchmentProduced: firstBatch ? fmt(firstBatch.parchmentProduced) : "—",
          statusLabel: firstBatch ? fmt(firstBatch.status) : "—",
        },

        qrCodes: {
          internalValue,
          internalId,
          externalValue: pd.externalQR || "",
          externalId: lotId,
        },

        statusType: "success",
        listStatusKey: "delivered",
        fieldIssues: {},
        hasResolvableDiscrepancy: false,
        resolutionConflicts: [],
      };
    },
    resetResolutionState() {
      this.activePayloadOverride = null;
      const selections = {};
      const conflicts = this.record ? getResolvableFieldConflicts(this.record) : [];
      conflicts.forEach((c) => {
        selections[c.fieldKey] = "factory";
      });
      this.resolutionSelections = selections;
    },
    getConflictFieldLabel(fieldKey) {
      const key = FIELD_LABEL_KEYS[fieldKey];
      if (key === "outturnNumber") {
        return this.$t("memberDataSection.parchmentDelivery.outturnNumber");
      }
      if (key && this.$te(`${this.baseI18n}.${key}`)) {
        return this.$t(`${this.baseI18n}.${key}`);
      }
      return fieldKey;
    },
    getFieldDisplayValue(fieldKey, fallbackDisplay) {
      const conflict = this.detail?.resolutionConflicts?.find((c) => c.fieldKey === fieldKey);
      if (conflict && this.resolutionSelections[fieldKey]) {
        return this.resolutionSelections[fieldKey] === "factory"
          ? conflict.factoryValue
          : conflict.uploadedValue;
      }
      return fallbackDisplay;
    },
    getIssueTitle(issueMeta) {
      const keys = issueMeta?.i18n || BULK_ISSUE_I18N_KEYS[issueMeta?.issueType];
      if (!keys) return "";
      return this.$t(`${this.baseI18n}.${keys.title}`);
    },
    getIssueDescription(issueMeta) {
      const keys = issueMeta?.i18n || BULK_ISSUE_I18N_KEYS[issueMeta?.issueType];
      if (!keys) return "";
      const ctx = this.record?.issueContext || {};
      return this.$t(`${this.baseI18n}.${keys.description}`, ctx);
    },
    confirmResolution() {
      if (!this.record || !this.canConfirmResolution) return;
      const mergedPayload = mergeResolutionIntoPayload(this.record, this.resolutionSelections);
      this.activePayloadOverride = mergedPayload;
      this.$emit("resolve", {
        record: this.record,
        payload: mergedPayload,
        selections: { ...this.resolutionSelections },
      });
      this.$notify({
        type: "success",
        title: this.$t("success"),
        text: this.$t("memberDataSection.parchmentDelivery.bulkImport.resolutionSaved"),
      });
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
    onViewHistory() {
      this.historyDialogVisible = true;
    },
    onEdit() {
      this.$emit("edit", this.fetchedRecord || this.record);
    },
    downloadQrCodes() {
      this.$notify({
        type: "info",
        title: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadQr"),
        text: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadQrPending"),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.parchment-detail-card {
  border-radius: 4px;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
}

.parchment-detail-dialog__body {
  max-height: calc(90vh - 120px);
  overflow-y: auto;
  background: #fff;
}

.parchment-detail-divider {
  border-color: #e0e0e0;
  width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.parchment-detail-section__title {
  color: var(--secondary-color, #117443);
  font-weight: 600;
  font-size: 15px;
  line-height: 1.35;
}

.parchment-detail-plain-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
  font-size: 15px;
  line-height: 1.35;
}

.parchment-resolution-panel {
  border: 1px solid #ffa92a;
  border-radius: 4px;
  background-color: #fff8e6;
  padding: 16px;
}

.parchment-resolution-panel__title {
  font-weight: 600;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.parchment-resolution-panel__subtitle,
.parchment-resolution-panel__prompt {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
}

.parchment-resolution-panel__inner {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
}

.parchment-outturn-mismatch-alert {
  border: 1px solid #ffa92a;
  border-radius: 4px;
  background-color: #fff8e6;
  padding: 16px;
}

.parchment-outturn-mismatch-alert__title {
  font-weight: 600;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.parchment-outturn-mismatch-alert__body {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.45;
}

.parchment-resolution-table__header,
.parchment-resolution-table__row {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr;
  column-gap: 12px;
  align-items: center;
}

.parchment-resolution-table__header {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 12px;
}

.parchment-resolution-table__row + .parchment-resolution-table__row {
  margin-top: 12px;
}

.parchment-resolution-table__field-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.parchment-resolution-table__choice {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
  cursor: pointer;
}

.parchment-resolution-table__choice input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #00bd73;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.parchment-resolution-table__choice input:checked {
  border-color: #00bd73;
  background-color: #fff;
  background-image: radial-gradient(circle, #00bd73 0 42%, transparent 48%);
}

.parchment-resolution-table__choice input:checked + span {
  color: #00bd73;
}

.parchment-detail-collapse-header {
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.parchment-detail-collapse-header:hover {
  background-color: #fafafa;
}

.parchment-detail-grid-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;
  row-gap: 16px;
}

.parchment-detail-grid-row--spaced {
  margin-top: 16px;
}

.parchment-detail-grid-cell {
  min-width: 0;
}

.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.35;
  margin-bottom: 2px;
}

.field-value {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  word-break: break-word;
}

.field-value-row {
  min-height: 22px;
}

.field-value-secondary {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
}


.parchment-detail-issue-tooltip {
  max-width: 340px;
  line-height: 1.45;
  font-size: 13px;
}

.status-pill {
  display: inline-block;
  margin-top: 4px;
  padding: 5px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  border: 2px solid transparent;
}

.status-pill--success {
  background-color: #d4edda;
  border-color: #28a745;
  color: #28a745;
}

.status-pill--draft {
  background-color: #ffffff;
  border-color: #212121;
  color: #212121;
}

.status-pill--issues {
  background-color: #fff7ec;
  border-color: #ffa826;
  color: #ffa826;
}

.status-pill--failed {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #dc3545;
}

.parchment-detail-qr-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.parchment-detail-qr-group {
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
}

.parchment-detail-qr-block {
  min-width: 120px;
  min-height: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.parchment-detail-qr-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.parchment-detail-footer {
  background: #fff;
}

@media (max-width: 600px) {
  .parchment-detail-grid-row,
  .parchment-resolution-table__header,
  .parchment-resolution-table__row {
    grid-template-columns: 1fr;
  }

  .parchment-detail-qr-group {
    gap: 24px;
  }
}
</style>
